import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Modal from 'ui-library/modals/Modal';

import {FeedbackContextItem} from './LLMFeedbackConstants';
import {submitLLMFeedback} from './LLMFeedbackDAO';

export interface LLMFeedbackModalProps {
  /**
   * The title of the feedback modal
   */
  title: string;
  /**
   * Optional context items that the user can opt-out of including with their feedback
   */
  optionalContextItems: Array<FeedbackContextItem>;
  /**
   * Required context items that are not displayed to the user and always included with their feedback
   */
  requiredContextItems: Array<FeedbackContextItem>;
  onRequestClose: () => void;
}

export default function LLMFeedbackModal(props: LLMFeedbackModalProps): JSX.Element {
  const [llmFeedback, setLLMFeedback] = useState<string>('');
  const [selectedOptionalItemNames, setSelectedOptionalItemNames] = useState<Array<string>>(
    props.optionalContextItems.map((item) => item.name)
  );

  return (
    <Modal
      title={props.title}
      buttons={[{text: 'Submit feedback', submit: true, disabled: !llmFeedback}]}
      closeableOnBackgroundClick={true}
      closeableOnEscapeKey={true}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        // intentionally don't wait for this to complete to close the modal and let the user get back to work
        submitLLMFeedback(props.title, llmFeedback, [
          ...props.requiredContextItems,
          ...props.optionalContextItems.filter((item) => selectedOptionalItemNames.includes(item.name)),
        ]);
        props.onRequestClose();
      }}
    >
      <div className={css(styles.paddedGrid)}>
        <div>
          <label htmlFor="feedback">Let us know what went wrong or how we can improve</label>
          <textarea
            autoFocus={true}
            rows={6}
            value={llmFeedback}
            onChange={(e) => {
              setLLMFeedback(e.target.value);
            }}
            name="feedback"
          />
        </div>

        {props.optionalContextItems.length > 0 && (
          <div>
            <h4>Optional context</h4>
            {props.optionalContextItems.map((context) => (
              <div key={context.name} className={css(styles.optionalItem)}>
                <CheckboxInput
                  checked={selectedOptionalItemNames.includes(context.name)}
                  id={`select-${context.name}`}
                  label={context.name}
                  onChange={() => {
                    setSelectedOptionalItemNames((prev) => {
                      if (prev.includes(context.name)) {
                        return prev.filter((item) => item !== context.name);
                      }
                      return [...prev, context.name];
                    });
                  }}
                />
                {selectedOptionalItemNames.includes(context.name) && (
                  <pre className={css(styles.limitedHeight)}>{context.value}</pre>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  optionalItem: {
    display: 'grid',
    gap: 8,
  },
  paddedGrid: {
    display: 'grid',
    gap: 12,
  },
  limitedHeight: {
    maxHeight: 240,
    overflowY: 'auto',
  },
});
